import { IntlShape } from 'react-intl';
import { AUTH_FAQ, SUBSCRIPTION_WHAT_THERAPY_PLAN_IS, WHAT_PLAN_THERAPY } from '../../intercom-articles';
import type { TFunction } from 'i18next';

export type ErrorCodeType = {
  text: string;
  intercomArticleID?: number;
};
export type ErrorCodesType = { [key: string]: ErrorCodeType };

export const backendErrorCodes = (intl: IntlShape): ErrorCodesType => ({
  // general
  no_such_specialist: {
    text: intl.formatMessage({
      id: 'toast.backend.error.no_such_specialist',
      defaultMessage:
        'Вашого психолога не було знайдено, будь ласка перезавантажте додаток, якщо ця помилка повториться - зв`яжіться з нашою службою підтримки клієнтів, щоб отримати подальшу допомогу.',
    }),
  },
  no_such_client: {
    text: intl.formatMessage({
      id: 'toast.backend.error.no_such_client',
      defaultMessage:
        'Щось пішло не так, ми не знайшли Ваший обліковий запис, будь ласка перезавантажте додаток, якщо ця помилка повториться - зв`яжіться з нашою службою підтримки клієнтів, щоб отримати подальшу допомогу.',
    }),
  },
  no_such_consultations: {
    text: intl.formatMessage({
      id: 'toast.backend.error.no_such_consultations',
      defaultMessage:
        'Ваш сеанс не було знайдено, будь ласка перезавантажте додаток, якщо ця помилка повториться - зв`яжіться з нашою службою підтримки клієнтів, щоб отримати подальшу допомогу.',
    }),
  },
  no_such_subscription: {
    text: intl.formatMessage({
      id: 'toast.backend.error.no_such_subscription',
      defaultMessage: 'Ваша підписка закінчилась. Якщо це не так - будь ласка перезавантажте додаток.',
    }),
  },
  price_changed: {
    text: intl.formatMessage({
      id: 'toast.backend.error.price_changed',
      defaultMessage:
        'Схоже ціну за сеанс було змінено психологом, будь ласка перезавантажте додаток, щоб отримати оновлену ціну.',
    }),
  },
  operation_not_allowed: {
    text: intl.formatMessage({
      id: 'toast.backend.error.operation_not_allowed',
      defaultMessage: 'Ця операція не доступна клієнтам.',
    }),
  },
  // crud
  invalid_timezone: {
    text: intl.formatMessage({
      id: 'toast.backend.error.invalid_timezone',
      defaultMessage: 'Не вірний формат надісланної часової зони.',
    }),
  },
  // subscription
  user_have_more_than_1_active_subscription: {
    text: intl.formatMessage({
      id: 'toast.backend.error.user_have_more_than_1_active_subscription',
      defaultMessage:
        'Ви вже маєте активну підписку з ненульовим балансом з цим психологом. Все добре, нічого робити не потрібно.',
    }),
  },
  incomplete_subscription_status: {
    text: intl.formatMessage({
      id: 'toast.backend.error.incomplete_subscription_status',
      defaultMessage: 'У вас немає підписки. Щоб забронювати сеанс, будь ласка, купіть її.',
    }),
    intercomArticleID: WHAT_PLAN_THERAPY,
  },
  no_available_balance: {
    text: intl.formatMessage({
      id: 'toast.backend.error.no_available_balance',
      defaultMessage:
        'Ваш баланс сеансів нульовий. Будь ласка перезавантажте додаток, якщо не допоможе, тоді придбайте нову підписку щоб поповнити Ваш баланс.',
    }),
    intercomArticleID: SUBSCRIPTION_WHAT_THERAPY_PLAN_IS,
  },
  session_plan_not_available: {
    text: intl.formatMessage({
      id: 'toast.backend.error.session_plan_not_available',
      defaultMessage: 'Нажаль план який ви обрали не є доступним, спробуйте перезавантажити додаток та обрати інший.',
    }),
  },
  subscription_months_unavailable: {
    text: intl.formatMessage({
      id: 'toast.backend.error.subscription_months_unavailable',
      defaultMessage:
        'Нажаль період терапії який ви обрали не є доступним, спробуйте перезавантажити додаток та обрати інший.',
    }),
  },
  pause_was_used_for_provided_subscription: {
    text: intl.formatMessage({
      id: 'toast.backend.error.pause_was_used_for_provided_subscription',
      defaultMessage:
        'Ви вже 1 раз призупиняли Вашу підписку, а за правилами користування це можна робити лише 1 раз за період.',
    }),
    intercomArticleID: WHAT_PLAN_THERAPY,
  },
  subscription_multiple_currency: {
    text: intl.formatMessage({
      id: 'toast.backend.error.subscription_multiple_currency',
      defaultMessage:
        'У вас уже є або була підписка в іншій валюті. Щоб придбати нову підписку у новій валюті, зверніться до нашої служби підтримки клієнтів.',
    }),
  },
  subscription_already_canceled: {
    text: intl.formatMessage({
      id: 'toast.backend.error.subscription_already_canceled',
      defaultMessage: 'Підписку вже скасовано, будь ласка перезавантажте додаток.',
    }),
  },
  // firstPayment
  only_subscription_allowed: {
    text: intl.formatMessage({
      id: 'toast.backend.error.only_subscription_allowed',
      defaultMessage: 'Дані про оплату не завантажились, спробуйте перезавантажити додаток та записатись ще раз.',
    }),
  },
  // promotionalCode
  promocode_not_found: {
    text: intl.formatMessage({
      id: 'toast.backend.error.promocode_not_found',
      defaultMessage: 'Цей промокод не знайдено',
    }),
  },
  promocode_has_expired: {
    text: intl.formatMessage({
      id: 'toast.backend.error.promocode_has_expired',
      defaultMessage: 'Термін дії цього промо-коду закінчився',
    }),
  },
  promocode_already_used: {
    text: intl.formatMessage({
      id: 'toast.backend.error.promocode_already_used',
      defaultMessage: 'Ви вже використали цей промокод',
    }),
  },
  // booking
  slot_not_active: {
    text: intl.formatMessage({
      id: 'toast.backend.error.slot_not_active',
      defaultMessage: 'Час не є актуальним, спробуйте оновити сторінку та обрати інший',
    }),
  },
  slot_booked: {
    text: intl.formatMessage({
      id: 'toast.backend.error.slot_booked',
      defaultMessage: 'Цей час вже заброньовано. Оберіть, будь ласка, інший час.',
    }),
  },
  slot_prebooked: {
    text: intl.formatMessage({
      id: 'toast.backend.error.slot_prebooked',
      defaultMessage: 'Цей час вже зарезервовано на 5 хвилин. Почекайте 5 хвилин, чи оберіть інший час.',
    }),
  },
  session_was_canceled: {
    text: intl.formatMessage({
      id: 'toast.backend.error.session_was_canceled',
      defaultMessage: 'Цей сеанс вже було скасовано.',
    }),
  },
  session_was_not_found: {
    text: intl.formatMessage({
      id: 'toast.backend.error.session_was_not_found',
      defaultMessage: 'Цей сеасн не знайдено, швидше за все його вже було скасовано.',
    }),
  },
  session_cant_be_rescheduled: {
    text: intl.formatMessage({
      id: 'toast.backend.error.session_cant_be_rescheduled',
      defaultMessage: 'Неможливо перенести сеанс.',
    }),
  },
  // specialistReviews
  review_exist: {
    text: intl.formatMessage({
      id: 'toast.backend.error.review_exist',
      defaultMessage: 'Схоже ви вже залишали відгук про цього спеціаліста, дякуємо!',
    }),
  },
  review_not_found: {
    text: intl.formatMessage({
      id: 'toast.backend.error.review_not_found',
      defaultMessage: 'Схоже цей відгук був видалений адміністратором.',
    }),
  },
});

export const videoCallingErrorCodes = (intl: IntlShape): ErrorCodesType => ({
  UNEXPECTED_ERROR: {
    text: intl.formatMessage({
      id: 'video_calling.error.UNEXPECTED_ERROR',
      defaultMessage: 'UNEXPECTED_ERROR',
    }),
  },
  UNEXPECTED_RESPONSE: {
    text: intl.formatMessage({
      id: 'video_calling.error.UNEXPECTED_RESPONSE',
      defaultMessage: 'UNEXPECTED_RESPONSE',
    }),
  },
  INVALID_PARAMS: {
    text: intl.formatMessage({
      id: 'video_calling.error.INVALID_PARAMS',
      defaultMessage: 'INVALID_PARAMS',
    }),
  },
  NOT_SUPPORTED: {
    text: intl.formatMessage({
      id: 'video_calling.error.NOT_SUPPORTED',
      defaultMessage: 'UNEXPECTED_ERROR',
    }),
  },
  INVALID_OPERATION: {
    text: intl.formatMessage({
      id: 'video_calling.error.INVALID_OPERATION',
      defaultMessage: 'Не можливо виконати цю операцію в поточному стані, зверніться в службу підтримки.',
    }),
  },
  OPERATION_ABORTED: {
    text: intl.formatMessage({
      id: 'video_calling.error.OPERATION_ABORTED',
      defaultMessage: 'Повільна швидкість зєднання, будь ласка перезавантажте додаток.',
    }),
  },
  WEB_SECURITY_RESTRICT: {
    text: intl.formatMessage({
      id: 'video_calling.error.WEB_SECURITY_RESTRICT',
      defaultMessage: 'Обмеження політики безпеки браузера, перевірте налаштування браузера.',
    }),
  },
  NO_ACTIVE_STATUS: {
    text: intl.formatMessage({
      id: 'video_calling.error.NO_ACTIVE_STATUS',
      defaultMessage: 'Проєкт не активний, зверніться в службу  підтримки.',
    }),
  },
  NETWORK_TIMEOUT: {
    text: intl.formatMessage({
      id: 'video_calling.error.NETWORK_TIMEOUT',
      defaultMessage: 'NETWORK_TIMEOUT: повільна швидкість інтернету, будь ласка перезавантажте додаток.',
    }),
  },
  NETWORK_RESPONSE_ERROR: {
    text: intl.formatMessage({
      id: 'video_calling.error.NETWORK_RESPONSE_ERROR',
      defaultMessage: 'NETWORK_RESPONSE_ERROR: повільна швидкість інтернету, будь ласка перезавантажте додаток.',
    }),
  },
  NETWORK_ERROR: {
    text: intl.formatMessage({
      id: 'video_calling.error.NETWORK_ERROR',
      defaultMessage: 'NETWORK_ERROR: повільна швидкість інтернету, будь ласка перезавантажте додаток.',
    }),
  },
  WS_ABORT: {
    text: intl.formatMessage({
      id: 'video_calling.error.WS_ABORT',
      defaultMessage: 'Під час запиту на сервери Agora відключено WebSocket.',
    }),
  },
  WS_DISCONNECT: {
    text: intl.formatMessage({
      id: 'video_calling.error.WS_DISCONNECT',
      defaultMessage: 'WebSocket відключено перед запитом на сервери Agora.',
    }),
  },
  WS_ERR: {
    text: intl.formatMessage({
      id: 'video_calling.error.WS_ERR',
      defaultMessage: 'Помилка підключення WebSocket.',
    }),
  },
  ENUMERATE_DEVICES_FAILED: {
    text: intl.formatMessage({
      id: 'video_calling.error.ENUMERATE_DEVICES_FAILED',
      defaultMessage:
        'Не вдається перерахувати локальні пристрої, як правило, через обмеження браузера, перевірте налаштування браузера.',
    }),
  },
  DEVICE_NOT_FOUND: {
    text: intl.formatMessage({
      id: 'video_calling.error.DEVICE_NOT_FOUND',
      defaultMessage: 'Не вдається знайти вказаний пристрій, перевірте налаштування пристрою та його зʼєднання.',
    }),
  },
  TRACK_IS_DISABLED: {
    text: intl.formatMessage({
      id: 'video_calling.error.TRACK_IS_DISABLED',
      defaultMessage: 'Доріжка вимкнена, перевірте налаштування пристрою та його зʼєднання.',
    }),
  },
  SHARE_AUDIO_NOT_ALLOWED: {
    text: intl.formatMessage({
      id: 'video_calling.error.SHARE_AUDIO_NOT_ALLOWED',
      defaultMessage: 'Не вдається отримати доступ до аудіо, перевірте налаштування аудіо пристрою та його зʼєднання.',
    }),
  },
  CHROME_PLUGIN_NO_RESPONSE: {
    text: intl.formatMessage({
      id: 'video_calling.error.CHROME_PLUGIN_NO_RESPONSE',
      defaultMessage: 'Немає відповіді від розширення Chrome для демонстрації екрана.',
    }),
  },
  CHROME_PLUGIN_NOT_INSTALL: {
    text: intl.formatMessage({
      id: 'video_calling.error.CHROME_PLUGIN_NOT_INSTALL',
      defaultMessage: 'Розширення Chrome для демонстрації екрана не встановлено.',
    }),
  },
  MEDIA_OPTION_INVALID: {
    text: intl.formatMessage({
      id: 'video_calling.error.MEDIA_OPTION_INVALID',
      defaultMessage: 'MEDIA_OPTION_INVALID: Недійсний параметр для захвату медіа пристрою.',
    }),
  },
  CONSTRAINT_NOT_SATISFIED: {
    text: intl.formatMessage({
      id: 'video_calling.error.CONSTRAINT_NOT_SATISFIED',
      defaultMessage: 'CONSTRAINT_NOT_SATISFIED: Недійсний параметр для захвату медіа пристрою.',
    }),
  },
  PERMISSION_DENIED: {
    text: intl.formatMessage({
      id: 'video_calling.error.PERMISSION_DENIED',
      defaultMessage: 'Щоб відеодзвінок працював, надайте доступ до мікрофона та камери в налаштуваннях пристрою.',
    }),
  },
  NOT_READABLE: {
    text: intl.formatMessage({
      id: 'video_calling.error.NOT_READABLE',
      defaultMessage:
        'Незважаючи на те, що Ви надали дозвіл на використання пристрою, доступ до нього заборонено, можливо Ви використовуєте інший додаток, що використовує цей пристрій.',
    }),
  },
  FETCH_AUDIO_FILE_FAILED: {
    text: intl.formatMessage({
      id: 'video_calling.error.FETCH_AUDIO_FILE_FAILED',
      defaultMessage: 'Не вдається завантажити аудіофайл.',
    }),
  },
  READ_LOCAL_AUDIO_FILE_ERROR: {
    text: intl.formatMessage({
      id: 'video_calling.error.READ_LOCAL_AUDIO_FILE_ERROR',
      defaultMessage: 'Не вдається прочитати локальний аудіофайл.',
    }),
  },
  DECODE_AUDIO_FILE_FAILED: {
    text: intl.formatMessage({
      id: 'video_calling.error.DECODE_AUDIO_FILE_FAILED',
      defaultMessage:
        'Не вдається декодувати аудіофайл головним чином через те, що кодек аудіофайлу не підтримується WebAudio.',
    }),
  },
  UID_CONFLICT: {
    text: intl.formatMessage({
      id: 'video_calling.error.UID_CONFLICT',
      defaultMessage: 'Повторювані UID у каналі.',
    }),
  },
  INVALID_UINT_UID_FROM_STRING_UID: {
    text: intl.formatMessage({
      id: 'video_calling.error.INVALID_UINT_UID_FROM_STRING_UID',
      defaultMessage: 'Недійсний UID.',
    }),
  },
  CAN_NOT_GET_PROXY_SERVER: {
    text: intl.formatMessage({
      id: 'video_calling.error.CAN_NOT_GET_PROXY_SERVER',
      defaultMessage: 'Не вдається отримати IP-адреси служби хмарного проксі.',
    }),
  },
  CAN_NOT_GET_GATEWAY_SERVER: {
    text: intl.formatMessage({
      id: 'video_calling.error.CAN_NOT_GET_GATEWAY_SERVER',
      defaultMessage: 'Не вдається отримати IP-адреси серверів Gateway.',
    }),
  },
  INVALID_LOCAL_TRACK: {
    text: intl.formatMessage({
      id: 'video_calling.error.INVALID_LOCAL_TRACK',
      defaultMessage: 'INVALID_LOCAL_TRACK',
    }),
  },
  CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS: {
    text: intl.formatMessage({
      id: 'video_calling.error.CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS',
      defaultMessage: 'Cхоже Ви публікуєте кілька відеодоріжок одночасно, зверніться в службу підтримки.',
    }),
  },
  INVALID_REMOTE_USER: {
    text: intl.formatMessage({
      id: 'video_calling.error.INVALID_REMOTE_USER',
      defaultMessage: 'Психолог ще не підєднався до сеансу.',
    }),
  },
  REMOTE_USER_IS_NOT_PUBLISHED: {
    text: intl.formatMessage({
      id: 'video_calling.error.REMOTE_USER_IS_NOT_PUBLISHED',
      defaultMessage: 'REMOTE_USER_IS_NOT_PUBLISHED',
    }),
  },
  ERR_TOO_MANY_BROADCASTERS: {
    text: intl.formatMessage({
      id: 'video_calling.error.ERR_TOO_MANY_BROADCASTERS',
      defaultMessage: 'Перевищено ліміт хостів на сеансі.',
    }),
  },
  ERR_TOO_MANY_SUBSCRIBERS: {
    text: intl.formatMessage({
      id: 'video_calling.error.ERR_TOO_MANY_SUBSCRIBERS',
      defaultMessage: 'Кількість хостів, на які підписався користувач, перевищує ліміт.',
    }),
  },
});
export const firebaseErrorCodes = (intl: IntlShape): ErrorCodesType => ({
  missing_user: {
    text: intl.formatMessage({
      id: 'auth.errors.missing_user',
      defaultMessage: 'Користувача не знайдено, будь ласка перезавантажте додаток.',
    }),
  },
  missing_email: {
    text: intl.formatMessage({
      id: 'auth.errors.missing_email',
      defaultMessage:
        "Ваш Apple ID вже прив'язаний до Mindly, зв'яжіться зі службою підтримки, Вам допоможуть увійти в застосунок",
    }),
    intercomArticleID: AUTH_FAQ,
  },
  missing_name: {
    text: intl.formatMessage({
      id: 'auth.errors.missing_name',
      defaultMessage:
        "Ваш Apple ID вже прив'язаний до Mindly, зв'яжіться зі службою підтримки, Вам допоможуть увійти в застосунок",
    }),
    intercomArticleID: AUTH_FAQ,
  },
  missing_lastName: {
    text: intl.formatMessage({
      id: 'auth.errors.missing_lastName',
      defaultMessage:
        "Ваш Apple ID вже прив'язаний до Mindly, зв'яжіться зі службою підтримки, Вам допоможуть увійти в застосунок",
    }),
    intercomArticleID: AUTH_FAQ,
  },
  'auth/admin-restricted-operation': {
    text: intl.formatMessage({
      id: 'auth.errors.admin-restricted-operation',
      defaultMessage: 'Ця операція доступна лише адміністраторам.',
    }),
  },
  'auth/argument-error': {
    text: intl.formatMessage({
      id: 'auth.errors.argument-error',
      defaultMessage: 'Ця операція доступна лише адміністраторам.',
    }),
  },
  'auth/app-not-authorized': {
    text: intl.formatMessage({
      id: 'auth.errors.app-not-authorized',
      defaultMessage: 'Ця програма не має права використовувати автентифікацію Firebase із наданим ключем API.',
    }),
  },
  'auth/app-not-installed': {
    text: intl.formatMessage({
      id: 'auth.errors.app-not-installed',
      defaultMessage: 'Мобільна програма не встановлена на цьому пристрої.',
    }),
  },
  'auth/captcha-check-failed': {
    text: intl.formatMessage({
      id: 'auth.errors.captcha-check-failed',
      defaultMessage:
        'Наданий токен відповіді reCAPTCHA недійсний, прострочений, уже використаний або пов’язаний із ним домен не відповідає списку білих доменів.',
    }),
  },
  'auth/code-expired': {
    text: intl.formatMessage({
      id: 'auth.errors.code-expired',
      defaultMessage: 'Термін дії SMS-коду закінчився. Щоб повторити спробу, надішліть код підтвердження ще раз.',
    }),
  },
  'auth/cordova-not-ready': {
    text: intl.formatMessage({
      id: 'auth.errors.cordova-not-read',
      defaultMessage: 'Cordova framework не готовий.',
    }),
  },
  'auth/cors-unsupported': {
    text: intl.formatMessage({
      id: 'auth.errors.cors-unsupported',
      defaultMessage: 'Цей браузер не підтримується.',
    }),
  },
  'auth/credential-already-in-use': {
    text: intl.formatMessage({
      id: 'auth.errors.credential-already-in-use',
      defaultMessage: 'Ці облікові дані вже пов’язані з іншим обліковим записом користувача.',
    }),
  },
  'auth/requires-recent-login': {
    text: intl.formatMessage({
      id: 'auth.errors.requires-recent-login',
      defaultMessage:
        'Ця операція є конфіденційною та потребує повторної аутентифікації. Увійдіть знову, перш ніж повторити цей запит.',
    }),
  },
  'auth/dynamic-link-not-activated': {
    text: intl.formatMessage({
      id: 'auth.errors.dynamic-link-not-activated',
      defaultMessage: 'Активуйте динамічні посилання на консолі Firebase і погодьтеся з положеннями та умовами.',
    }),
  },
  'auth/email-change-needs-verification': {
    text: intl.formatMessage({
      id: 'auth.errors.email-change-needs-verification',
      defaultMessage: 'Багатофакторні користувачі завжди повинні мати підтверджену електронну адресу.',
    }),
  },
  'auth/email-already-in-use': {
    text: intl.formatMessage({
      id: 'auth.errors.email-already-in-use',
      defaultMessage: 'Адреса електронної пошти вже використовується іншим користувачем.',
    }),
  },
  'auth/expired-action-code': {
    text: intl.formatMessage({
      id: 'auth.errors.expired-action-code',
      defaultMessage: 'Термін дії коду минув.',
    }),
  },
  'auth/cancelled-popup-request': {
    text: intl.formatMessage({
      id: 'auth.errors.cancelled-popup-request',
      defaultMessage: 'Цю операцію скасовано через відкриття іншого конфліктного спливаючого вікна.',
    }),
  },
  'auth/internal-error': {
    text: intl.formatMessage({
      id: 'auth.errors.internal-error',
      defaultMessage: 'Сталася внутрішня помилка.',
    }),
  },
  'auth/invalid-app-credential': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-app-credential',
      defaultMessage:
        'Запит на перевірку телефону містить недійсний верифікатор програми. Відповідь маркера reCAPTCHA недійсна або термін дії минув.',
    }),
  },
  'auth/invalid-app-id': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-app-id',
      defaultMessage: 'Ідентифікатор мобільного додатка не зареєстровано для поточного проекту.',
    }),
  },
  'auth/invalid-user-token': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-user-token',
      defaultMessage:
        'Облікові дані цього користувача недійсні для цього проекту. Це може статися, якщо маркер користувача було підроблено або якщо користувач не для проекту, пов’язаного з цим ключем API.',
    }),
  },
  'auth/invalid-auth-event': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-auth-event',
      defaultMessage: 'Сталася внутрішня помилка.',
    }),
  },
  'auth/invalid-verification-code': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-verification-code',
      defaultMessage:
        'Код підтвердження SMS, використаний для створення облікових даних телефону, недійсний. Повторно надішліть SMS із кодом підтвердження та обов’язково використовуйте код підтвердження, наданий користувачем.',
    }),
  },
  'auth/invalid-continue-uri': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-continue-uri',
      defaultMessage: 'URL-адреса продовження, надана в запиті, недійсна.',
    }),
  },
  'auth/invalid-cordova-configuration': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-cordova-configuration',
      defaultMessage:
        'Щоб увімкнути вхід OAuth, необхідно встановити такі плагіни Cordova: cordova-plugin-buildinfo, cordova-universal-links-plugin, cordova-plugin-browsertab, cordova-plugin-inappbrowser та cordova-plugin-customurlscheme.',
    }),
  },
  'auth/invalid-custom-token': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-custom-token',
      defaultMessage:
        'З міркувань безпеки, у вас була максимум 1 година на реєстрацію. Оскільки цей час вийшов, ваші дані були видалені. Але ви можете зареєструватися та пройти процедуру онбордингу знову.',
    }),
  },
  'auth/invalid-dynamic-link-domain': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-dynamic-link-domain',
      defaultMessage: 'Наданий домен динамічного посилання не налаштований або авторизований для поточного проекту.',
    }),
  },
  'auth/invalid-email': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-email',
      defaultMessage: 'Адреса електронної пошти має неправильний формат.',
    }),
  },
  'auth/invalid-api-key': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-api-key',
      defaultMessage: 'Ваш ключ API недійсний.',
    }),
  },
  'auth/invalid-cert-hash': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-cert-hash',
      defaultMessage: 'Наданий хеш сертифіката SHA-1 недійсний.',
    }),
  },
  'auth/invalid-credential': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-credential',
      defaultMessage: 'Надані облікові дані аутентифікації неправильні або термін дії минув.',
    }),
  },
  'auth/invalid-message-payload': {
    text: 'The email template corresponding to this action contains invalid characters in its message. Please fix by going to the Auth email templates section in the Firebase Console.',
  },
  'auth/invalid-multi-factor-session': {
    text: 'The request does not contain a valid proof of first factor successful sign-in.',
  },
  'auth/invalid-oauth-provider': {
    text: 'EmailAuthProvider is not supported for this operation. This operation only supports OAuth providers.',
  },
  'auth/invalid-oauth-client-id': {
    text: 'The OAuth client ID provided is either invalid or does not match the specified API key.',
  },
  'auth/unauthorized-domain': {
    text: 'This domain is not authorized for OAuth operations for your Firebase project. Edit the list of authorized domains from the Firebase console.',
  },
  'auth/invalid-action-code': {
    text: 'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.',
  },
  'auth/wrong-password': {
    text: intl.formatMessage({
      id: 'auth.errors.wrong-password',
      defaultMessage: 'Невірний пароль або такого користувача не існує.',
    }),
  },
  'auth/invalid-persistence-type': {
    text: 'The specified save type is invalid. It can be local only, session only, or never.',
  },
  'auth/invalid-phone-number': {
    text: intl.formatMessage({
      id: 'auth.errors.invalid-phone-number',
      defaultMessage: 'Формат наданого номера телефону неправильний.',
    }),
  },
  'auth/invalid-provider-id': {
    text: 'The specified provider ID is invalid.',
  },
  'auth/invalid-recipient-email': {
    text: 'The email corresponding to this action failed to send as the provided recipient email address is invalid.',
  },
  'auth/invalid-sender': {
    text: 'The email template corresponding to this action contains an invalid sender email or name. Please fix by going to the Auth email templates section in the Firebase Console.',
  },
  'auth/invalid-verification-id': {
    text: 'The verification ID used to create the phone auth credential is invalid.',
  },
  'auth/invalid-tenant-id': {
    text: "The Auth instance's tenant ID is invalid.",
  },
  'auth/multi-factor-info-not-found': {
    text: 'The user does not have a second factor matching the identifier provided.',
  },
  'auth/multi-factor-auth-required': {
    text: 'Proof of ownership of a second factor is required to complete sign-in.',
  },
  'auth/missing-android-pkg-name': {
    text: 'An Android Package Name must be provided if the Android App is required to be installed.',
  },
  'auth/auth-domain-config-required': {
    text: 'Be sure to include authDomain when calling firebase.initializeApp(), by following the instructions in the Firebase console.',
  },
  'auth/missing-app-credential': {
    text: 'The phone verification request is missing an application verifier assertion. A reCAPTCHA response token needs to be provided.',
  },
  'auth/missing-verification-code': {
    text: 'The phone auth credential was created with an empty SMS verification code.',
  },
  'auth/missing-continue-uri': {
    text: 'A continue URL must be provided in the request.',
  },
  'auth/missing-iframe-start': { text: 'An internal error has occurred.' },
  'auth/missing-ios-bundle-id': {
    text: 'An iOS Bundle ID must be provided if an App Store ID is provided.',
  },
  'auth/missing-multi-factor-info': {
    text: 'No second factor identifier is provided.',
  },
  'auth/missing-multi-factor-session': {
    text: 'The request is missing proof of first factor successful sign-in.',
  },
  'auth/missing-or-invalid-nonce': {
    text: 'The request does not contain a valid nonce. This can occur if the SHA-256 hash of the provided raw nonce does not match the hashed nonce in the ID token payload.',
  },
  'auth/missing-phone-number': {
    text: 'To send verification codes, provide a phone number for the recipient.',
  },
  'auth/missing-verification-id': {
    text: 'The phone auth credential was created with an empty verification ID.',
  },
  'auth/app-deleted': {
    text: 'This instance of FirebaseApp has been deleted.',
  },
  'auth/account-exists-with-different-credential': {
    text: intl.formatMessage({
      id: 'auth.errors.account-exists-with-different-credential',
      defaultMessage:
        'Уже існує обліковий запис із тією самою адресою електронної пошти, але іншими обліковими даними для входу. Увійдіть за допомогою способу який пов’язаний з цією електронною адресою.',
    }),
  },
  'auth/network-request-failed': {
    text: 'A network error (such as timeout, interrupted connection or unreachable host) has occurred.',
  },
  'auth/no-auth-event': { text: 'An internal error has occurred.' },
  'auth/no-such-provider': {
    text: 'User was not linked to an account with the given provider.',
  },
  'auth/null-user': {
    text: 'A null user object was provided as the argument for an operation which requires a non-null user object.',
  },
  'auth/operation-not-allowed': {
    text: 'The given sign-in provider is disabled for this Firebase project. Enable it in the Firebase console, under the sign-in method tab of the Auth section.',
  },
  'auth/operation-not-supported-in-this-environment': {
    text: 'This operation is not supported in the environment this application is running on. "location.protocol" must be http, https or chrome-extension and web storage must be enabled.',
  },
  'auth/popup-blocked': {
    text: 'Unable to establish a connection with the popup. It may have been blocked by the browser.',
  },
  'auth/popup-closed-by-user': {
    text: 'The popup has been closed by the user before finalizing the operation.',
  },
  'auth/provider-already-linked': {
    text: 'User can only be linked to one identity for the given provider.',
  },
  'auth/quota-exceeded': {
    text: "The project's quota for this operation has been exceeded.",
  },
  'auth/redirect-cancelled-by-user': {
    text: 'The redirect operation has been cancelled by the user before finalizing.',
  },
  'auth/redirect-operation-pending': {
    text: 'A redirect sign-in operation is already pending.',
  },
  'auth/rejected-credential': {
    text: 'The request contains malformed or mismatching credentials.',
  },
  'auth/second-factor-already-in-use': {
    text: 'The second factor is already enrolled on this account.',
  },
  'auth/maximum-second-factor-count-exceeded': {
    text: 'The maximum allowed number of second factors on a user has been exceeded.',
  },
  'auth/tenant-id-mismatch': {
    text: "The provided tenant ID does not match the Auth instance's tenant ID",
  },
  'auth/timeout': {
    text: 'The operation has timed out.',
  },
  'auth/user-token-expired': {
    text: intl.formatMessage({
      id: 'auth.errors.user-token-expired',
      defaultMessage: 'Ваші дані більше недійсні. Увідіть будь-ласка знову.',
    }),
  },
  'auth/too-many-requests': {
    text: intl.formatMessage({
      id: 'auth.errors.too-many-requests',
      defaultMessage:
        'Ми заблокували всі запити з цього пристрою через незвичайну активність. Спробуйте ще раз пізніше.',
    }),
  },
  'auth/unauthorized-continue-uri': {
    text: 'The domain of the continue URL is not whitelisted.  Please whitelist the domain in the Firebase console.',
  },
  'auth/unsupported-first-factor': {
    text: 'Enrolling a second factor or signing in with a multi-factor account requires sign-in with a supported first factor.',
  },
  'auth/unsupported-persistence-type': {
    text: 'The current environment does not support the specified persistence type.',
  },
  'auth/unsupported-tenant-operation': {
    text: 'This operation is not supported in a multi-tenant context.',
  },
  'auth/unverified-email': {
    text: intl.formatMessage({
      id: 'auth.errors.unverified-email',
      defaultMessage: 'Для операції потрібна підтверджена електронна адреса.',
    }),
  },
  'auth/user-cancelled': {
    text: 'The user did not grant your application the permissions it requested.',
  },
  'auth/user-not-found': {
    text: intl.formatMessage({
      id: 'auth.errors.user-not-found',
      defaultMessage: 'Користувача не знайдено, перевірте введені дані та спробуйте ще раз',
    }),
  },
  'auth/user-disabled': {
    text: intl.formatMessage({
      id: 'auth.errors.user-disabled',
      defaultMessage: 'Обліковий запис користувача було відключено адміністратором.',
    }),
  },
  'auth/user-mismatch': {
    text: 'The supplied credentials do not correspond to the previously signed in user.',
  },
  'auth/weak-password': {
    text: intl.formatMessage({
      id: 'auth.errors.weak-password',
      defaultMessage: 'Пароль має містити 6 або більше символів.',
    }),
  },
  'auth/web-storage-unsupported': {
    text: 'This browser is not supported or 3rd party cookies and data may be disabled.',
  },
  popup_blocked_by_browser: {
    text: intl.formatMessage({
      id: 'auth.errors.popup_blocked_by_browser',
      defaultMessage:
        'У Safari за замовчуванням спливаючі вікна заблоковані.  Розблокуйте їх у Налаштування – Safari, щоб авторизація через Apple запрацювала.',
    }),
  },
});

export const stripeErrorCodes = (intl: IntlShape): ErrorCodesType => ({
  incomplete_number: {
    text: intl.formatMessage({
      id: 'payments.stripe.incomplete_number',
      defaultMessage: 'Для оплати потрібно ввести картку',
    }),
  },
  generic_decline: {
    text: intl.formatMessage({
      id: 'payments.stripe.generic_decline',
      defaultMessage: 'Вашу картку відхилено.',
    }),
  },
  incorrect_number: {
    text: intl.formatMessage({
      id: 'payments.stripe.incorrect_number',
      defaultMessage: 'Номер картки неправильний.',
    }),
  },
  invalid_number: {
    text: intl.formatMessage({
      id: 'payments.stripe.invalid_number',
      defaultMessage: 'Номер картки не є дійсним',
    }),
  },
  invalid_expiry_month: {
    text: intl.formatMessage({
      id: 'payments.stripe.invalid_expiry_month',
      defaultMessage: 'Місяць закінчення терміну дії картки недійсний.',
    }),
  },
  invalid_expiry_year: {
    text: intl.formatMessage({
      id: 'payments.stripe.invalid_expiry_year',
      defaultMessage: 'Рік закінчення терміну дії картки недійсний.',
    }),
  },
  invalid_cvc: {
    text: intl.formatMessage({
      id: 'payments.stripe.invalid_cvc',
      defaultMessage: 'Код CVC недійсний.',
    }),
  },
  expired_card: {
    text: intl.formatMessage({
      id: 'payments.stripe.expired_card',
      defaultMessage: 'Термін дії картки закінчився.',
    }),
  },
  incorrect_cvc: {
    text: intl.formatMessage({
      id: 'payments.stripe.incorrect_cvc',
      defaultMessage: 'СVC код неправильний.',
    }),
  },
  card_declined: {
    text: intl.formatMessage({
      id: 'payments.stripe.card_declined',
      defaultMessage: 'Картка відхилена.',
    }),
  },
  processing_error: {
    text: intl.formatMessage({
      id: 'payments.stripe.processing_error',
      defaultMessage: 'Під час обробки картки сталася помилка.',
    }),
  },
  insufficient_funds: {
    text: intl.formatMessage({
      id: 'payments.stripe.insufficient_funds',
      defaultMessage: 'Недостатньо коштів на картці.',
    }),
  },
  lost_card: {
    text: intl.formatMessage({
      id: 'payments.stripe.lost_card',
      defaultMessage: 'Платіж відхилено, оскільки повідомляється, що картку втрачено.',
    }),
  },
  stolen_card: {
    text: intl.formatMessage({
      id: 'payments.stripe.stolen_card',
      defaultMessage: 'Платіж відхилено, оскільки повідомляється, що картку вкрадено.',
    }),
  },
  rate_limit: {
    text: intl.formatMessage({
      id: 'payments.stripe.rate_limit',
      defaultMessage:
        'Сталася помилка через те, що запити занадто швидко потрапляють на API. Повідомте нас, якщо ви постійно стикаєтеся з цією помилкою.',
    }),
  },
  payment_intent_authentication_failure: {
    text: intl.formatMessage({
      id: 'payments.stripe.payment_intent_authentication_failure',
      defaultMessage: 'Ви не пройшли 3D верифікацію. Спробуйте ще раз.',
    }),
  },
});

export const couponErrorCodes = (t: TFunction): ErrorCodesType => ({
  UNKNOWN_COUPON: { text: t('gift-certificates:checkout.gift_card.unavailable_error') },
  COUPON_PREVIEW_OUTDATED: { text: t('gift-certificates:checkout.gift_card.unavailable_error') },
  INVALID_COUPON_AMOUNT: { text: t('gift-certificates:checkout.gift_card.unavailable_error') },
});
